import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name?.split(" ")[0]}!`);
      nav(u.role === "superadmin" ? "/admin" : u.role === "store" ? "/store" : "/");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#1B5E20] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #FFB300 0, transparent 40%)" }} />
        <BrandLogo size="lg" light showTagline />
        <div className="relative">
          <h2 className="font-display text-4xl font-extrabold leading-tight">Welcome back to the<br /><span className="text-[#FFB300]">soup kitchen.</span></h2>
          <p className="text-white/70 mt-3">Log in to reorder your favorites in seconds.</p>
        </div>
        <p className="text-white/50 text-sm relative">Created by Michael S. Carlos</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden mb-8"><BrandLogo size="md" showTagline /></div>
          <h1 className="font-display text-3xl font-bold">Log in</h1>
          <p className="text-muted-foreground mb-6">Enter your credentials to continue.</p>
          {err && <div className="bg-red-50 text-[#C62828] text-sm rounded-lg p-3 mb-4" data-testid="login-error">{err}</div>}
          <div className="space-y-4">
            <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="mt-1 h-11" /></div>
            <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="mt-1 h-11" /></div>
          </div>
          <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full mt-6 h-12 bg-[#1B5E20] hover:bg-[#144718] font-semibold">{busy ? "Logging in…" : "Log in"}</Button>
          <p className="text-sm text-center mt-5 text-muted-foreground">No account? <Link to="/register" className="text-[#1B5E20] font-semibold" data-testid="to-register">Create one</Link></p>
          <p className="text-xs text-center mt-3"><Link to="/" className="text-muted-foreground">← Back to store</Link></p>
        </form>
      </div>
    </div>
  );
}
